import type { LocationQuery } from 'vue-router'
import type { PricingCategory } from '~/composables/types/event'

export type TicketCategoryType =
  | 'ADULT'
  | 'YOUTH'
  | 'CHILD'
  | 'SENIOR'
  | 'STUDENT'

/** Query param suffix (lowercase) → ticket category key */
export const ADD_TO_CART_CATEGORY_QUERY_PARAMS: Record<
  string,
  TicketCategoryType
> = {
  add_event_to_cart_adult: 'ADULT',
  add_event_to_cart_child: 'CHILD',
  add_event_to_cart_youth: 'YOUTH',
  add_event_to_cart_senior: 'SENIOR',
  add_event_to_cart_student: 'STUDENT',
}

const getQueryParamValue = (
  query: LocationQuery,
  key: string
): string | undefined => {
  const value = query[key]
  if (value == null) return undefined
  const raw = Array.isArray(value) ? value[0] : value
  return raw?.toString()
}

const parsePositiveInteger = (
  value: string | undefined
): number | undefined => {
  if (!value) return undefined
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 0) return undefined
  return parsed
}

/**
 * Reads category-specific add-to-cart deep-link query params and returns
 * quantities keyed by ticket category (e.g. { ADULT: 2, CHILD: 3 }).
 */
export const parseAddToCartCategoryQuantitiesFromQuery = (
  query: LocationQuery
): Record<string, number> | undefined => {
  const quantities: Record<string, number> = {}

  for (const [param, category] of Object.entries(
    ADD_TO_CART_CATEGORY_QUERY_PARAMS
  )) {
    const amount = parsePositiveInteger(getQueryParamValue(query, param))
    if (amount !== undefined) {
      quantities[category] = amount
    }
  }

  return Object.keys(quantities).length > 0 ? quantities : undefined
}

export const hasAddToCartCategoryQuantities = (query: LocationQuery): boolean =>
  parseAddToCartCategoryQuantitiesFromQuery(query) !== undefined

/**
 * Merges query-derived quantities with the event's pricing categories.
 * Categories not present on the event are ignored; missing categories default to 0.
 */
export const mergeAddToCartCategoryQuantities = (
  categories: PricingCategory[],
  queryQuantities: Record<string, number>
): Record<string, number> => {
  return categories.reduce(
    (acc, category) => {
      acc[category.ticket_category] =
        queryQuantities[category.ticket_category] ?? 0
      return acc
    },
    {} as Record<string, number>
  )
}
